import { useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PARTY_ROLES } from "@rentorng/core";
import { api } from "@/lib/api";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import { Alert, Button, Card, Field, Input, PageHeader, Select, Spinner, Textarea } from "@/components/ui";
import { PhoneInput } from "@/components/phone-input";
import { IconPlus, IconTrash } from "@/components/icons";
import { OptionPicker } from "@/components/option-picker";

/**
 * The client form.
 *
 * Roles are checkboxes, not a dropdown, because one person is regularly both a tenant and a
 * landlord. Two phone numbers are offered as standard: the one that rings and the one that
 * answers are often different.
 */

const ID_TYPES = ["NIN", "Passport", "Driver's licence", "Voter's card", "CAC number", "Other"];

export function ClientFormPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  /**
   * Where the agency's dealings with this person stand.
   *
   * Null until somebody says otherwise, and null is a real answer rather than a missing one: a
   * status nobody has set is *nothing*, not "Lead", and filing every client the moment they are
   * created would be the software making a judgement on the agency's behalf. Held in state rather
   * than read off the form at submit time because the picker can add to its own list, and an entry
   * created in the dialog has to arrive already chosen.
   */
  const [statusOptionId, setStatusOptionId] = useState<string | null>(null);

  /**
   * The roles, the phone numbers and the status: every one of them above the loading guard.
   *
   * **They were below it, and the edit screen was a blank page.** A hook called after an early
   * return runs on some renders and not others: the first render of `/app/clients/:id/edit` is the
   * spinner, which returned before reaching them, and the second — with the record in hand — called
   * two more than the first. React refuses outright, "rendered more hooks than during the previous
   * render", and the whole screen unmounts to nothing with the reason only in the console. The
   * *new* client form was unaffected, because nothing is loading and the guard never fires, which
   * is why it went unnoticed: the way in that is used ten times a day worked perfectly.
   *
   * Nothing may be added between here and the guard that is not a hook, and nothing below it that
   * is one.
   */
  const [roles, setRoles] = useState<string[]>([search.get("role") ?? "tenant"]);
  /** Always at least one row, so the form never opens with nowhere to type. */
  const [phones, setPhones] = useState<Array<{ number: string; whatsapp: boolean }>>([
    { number: "", whatsapp: false },
  ]);
  /** Which record the three above were filled in from, so it happens once and not on every render. */
  const [seededFor, setSeededFor] = useState<string | null>(null);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["party", id],
    queryFn: () => api.get<{ party: Record<string, string | number | string[] | null> }>(`/parties/${id}`),
    enabled: editing,
  });

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) =>
      editing ? api.patch<{ id: string }>(`/parties/${id}`, input) : api.post<{ id: string }>("/parties", input),
    invalidates: [["parties"], ["party", id ?? ""]],
    onSuccess: (data) => navigate(`/app/clients/${data.id ?? id}`),
  });

  const party = (existing?.party ?? {}) as Record<string, string | number | string[] | null>;

  /*
    Filled in from the record during the render, not in an effect.

    An effect runs after the paint, so the form would draw once with no roles ticked and one empty
    phone row and then correct itself — and the boxes underneath are uncontrolled, taking their
    `defaultValue` at the moment they mount, so a first paint with an empty record is a form that
    stays empty. Doing it here means the guard below is still the thing that decides when the form
    mounts at all, and it mounts already filled in.

    Guarded on which record it was done for rather than on a boolean, so opening a second client
    after a first replaces the answers instead of keeping them.

    The roles decide what the rest of the form asks for — a vendor has no next of kin and a buyer
    has no employer — which is why they are held here rather than read off the checkboxes at submit
    time: the form has to react as they are ticked.
  */
  if (existing?.party && seededFor !== (id ?? null)) {
    setSeededFor(id ?? null);
    setRoles((party.roles as string[] | undefined) ?? [search.get("role") ?? "tenant"]);
    const saved = (party.phones as unknown as Array<{ number: string; whatsapp: boolean }> | undefined) ?? [];
    setPhones(saved.length ? saved : [{ number: "", whatsapp: false }]);
    setStatusOptionId((party.statusOptionId as string | null) ?? null);
  }

  if (editing && isLoading) return <Spinner />;

  const setPhone = (index: number, patch: Partial<{ number: string; whatsapp: boolean }>) =>
    setPhones((current) => current.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  const has = (role: string) => roles.includes(role);
  const toggleRole = (role: string, on: boolean) =>
    setRoles((current) => (on ? [...new Set([...current, role])] : current.filter((entry) => entry !== role)));

  /** Somebody who lives somewhere: what they do, who to call, who vouches for them. */
  const isOccupier = has("tenant") || has("guarantor");
  /** Somebody we pay, or take a cut from. */
  const isPaid = has("landlord") || has("vendor");

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    const selectedRoles = roles;
    save.mutate({
      name: values.name,
      roles: selectedRoles.length ? selectedRoles : ["tenant"],
      // `phone` is derived on the server from the first entry, so it is not sent: two sources
      // for the same fact is one too many.
      phones: phones.filter((entry) => entry.number.trim()),
      email: nullIfBlank(values.email),
      address: nullIfBlank(values.address),
      occupation: nullIfBlank(values.occupation),
      employer: nullIfBlank(values.employer),
      idType: nullIfBlank(values.idType),
      idNumber: nullIfBlank(values.idNumber),
      nextOfKin: nullIfBlank(values.nextOfKin),
      nextOfKinPhone: nullIfBlank(values.nextOfKinPhone),
      bankName: nullIfBlank(values.bankName),
      bankAccountName: nullIfBlank(values.bankAccountName),
      bankAccountNumber: nullIfBlank(values.bankAccountNumber),
      statusOptionId,
      notes: nullIfBlank(values.notes),
    });
  };

  return (
    <div className="max-w-2xl">
      <PageHeader
        title={editing ? `Edit ${party.name}` : "Add a client"}
        back={editing ? { to: `/app/clients/${id}`, label: String(party.name ?? "Back") } : { to: "/app/clients", label: "Clients" }}
      />

      <Card>
        <form onSubmit={submit} className="space-y-4">
          {save.errorMessage ? <Alert tone="danger">{save.errorMessage}</Alert> : null}

          <Field label="Full name" htmlFor="name" required error={save.fieldErrors.name}>
            <Input id="name" name="name" required defaultValue={String(party.name ?? "")} placeholder="Adaeze Nwosu" />
          </Field>

          <fieldset>
            <legend className="label">Roles</legend>
            <div className="flex flex-wrap gap-3">
              {PARTY_ROLES.map((role) => (
                <label key={role.value} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    name="roles"
                    value={role.value}
                    checked={roles.includes(role.value)}
                    onChange={(event) => toggleRole(role.value, event.target.checked)}
                    className="size-4 rounded border-ink-300"
                  />
                  {role.label}
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-ink-500">Tick more than one where it applies — the same person is often both.</p>
          </fieldset>

          {/*
            One status, not a set, and it sits with the roles because it answers the question
            beside them.

            The set-shaped question about a client is already answered above: the same person is a
            tenant here and a landlord there, which is exactly what roles are for. A *status* is a
            stage in one relationship, and what it is drawn as is one mark beside a name — a set
            would leave the clients list choosing which of three marks to show, and nobody could
            explain that choice to the person reading it. Per client rather than per role for the
            same reason: the clients list is not role-scoped, so a per-role status would leave it
            with nothing to draw. See ADR-0134.
          */}
          <Field
            label="Status"
            htmlFor="statusOptionId"
            hint="Where this relationship stands. Left unset, no mark is drawn beside their name."
          >
            <OptionPicker
              kind="client_status"
              id="statusOptionId"
              value={statusOptionId}
              onChange={setStatusOptionId}
              emptyLabel="Not set"
              ariaLabel="Client status"
            />
          </Field>

          {/*
            One list rather than three boxes.
            "Phone, other phone, WhatsApp" could not hold a third number, could not say that two
            of them are both on WhatsApp, and left "which number do I message?" unanswerable.
            The first row is the one that gets rung and the one duplicates are checked against.
          */}
          <Field label="Phone numbers" group hint="The first is the one we ring, and the one we check for duplicates.">
            <div className="space-y-2">
              {phones.map((entry, index) => (
                /* Keyed on the record as well as the position: the box is uncontrolled, so it
                   only reads its `defaultValue` when it mounts, and a key that did not change
                   between two clients would show the first one's numbers on the second. */
                <div key={`${seededFor ?? "new"}-${index}`} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  <div className="min-w-0 flex-1">
                    <PhoneInput
                      key={`phone-${seededFor ?? "new"}-${index}`}
                      defaultValue={entry.number}
                      onValue={(value) => setPhone(index, { number: value })}
                    />
                  </div>

                  <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm text-ink-600">
                    <input
                      type="checkbox"
                      checked={entry.whatsapp}
                      onChange={(event) => setPhone(index, { whatsapp: event.target.checked })}
                      className="size-4 rounded border-ink-300"
                    />
                    WhatsApp
                  </label>

                  {phones.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => setPhones((current) => current.filter((_, i) => i !== index))}
                      aria-label="Remove this number"
                      className="shrink-0 rounded-[var(--radius-control)] p-2 text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                    >
                      <IconTrash size={16} />
                    </button>
                  ) : null}
                </div>
              ))}

              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setPhones((current) => [...current, { number: "", whatsapp: false }])}
              >
                <IconPlus size={15} />
                Add another number
              </Button>
            </div>
          </Field>

          <Field label="Email" htmlFor="email" hint="Email reminders and receipts go here." error={save.fieldErrors.email}>
            <Input id="email" name="email" type="email" placeholder="adaeze@example.com" defaultValue={String(party.email ?? "")} />
          </Field>

          <Field label="Address" htmlFor="address">
            <Input id="address" name="address" placeholder="14 Bourdillon Road, Ikoyi" defaultValue={String(party.address ?? "")} />
          </Field>

          {isOccupier ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Occupation" htmlFor="occupation">
                <Input
                  id="occupation"
                  name="occupation"
                  placeholder="Architect"
                  defaultValue={String(party.occupation ?? "")}
                />
              </Field>
              <Field label="Employer" htmlFor="employer">
                <Input
                  id="employer"
                  name="employer"
                  placeholder="Adeyemi & Partners"
                  defaultValue={String(party.employer ?? "")}
                />
              </Field>
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="ID type" htmlFor="idType">
              <Select id="idType" name="idType" defaultValue={String(party.idType ?? "")}>
                <option value="">Not recorded</option>
                {ID_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ID number" htmlFor="idNumber">
              <Input id="idNumber" name="idNumber" placeholder="12345678901" defaultValue={String(party.idNumber ?? "")} />
            </Field>
          </div>

          {has("tenant") ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Next of kin" htmlFor="nextOfKin">
                <Input
                  id="nextOfKin"
                  name="nextOfKin"
                  placeholder="Chinwe Nwosu"
                  defaultValue={String(party.nextOfKin ?? "")}
                />
              </Field>
              <Field label="Next of kin phone" htmlFor="nextOfKinPhone">
                <PhoneInput id="nextOfKinPhone" name="nextOfKinPhone" defaultValue={String(party.nextOfKinPhone ?? "")} />
              </Field>
            </div>
          ) : null}

          {isPaid ? (
          <div className="rounded-[var(--radius-surface)] border border-ink-200 p-3">
            <p className="label mb-1">Bank details</p>
            <div className="mt-3 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Bank" htmlFor="bankName">
                  <Input id="bankName" name="bankName" placeholder="GTBank" defaultValue={String(party.bankName ?? "")} />
                </Field>
                <Field label="Account number" htmlFor="bankAccountNumber">
                  <Input id="bankAccountNumber" name="bankAccountNumber" placeholder="0123456789" defaultValue={String(party.bankAccountNumber ?? "")} />
                </Field>
              </div>
              <Field label="Account name" htmlFor="bankAccountName">
                <Input id="bankAccountName" name="bankAccountName" placeholder="Adaeze Nwosu" defaultValue={String(party.bankAccountName ?? "")} />
              </Field>
              {/*
                A commission rate was asked for here and is gone.

                The product assumed the agency's income was a percentage cut of the landlord's
                rent. It is not: the rent is the landlord's, whole and untouched, and the agency's
                income is the fee the tenant pays on top of it, on its own line of the same
                invoice. Nothing is taken off a landlord, so there is nothing to agree a rate for —
                and a switched-off field would be a setting every agency has to read and dismiss.
                See ADR-0131.
              */}
            </div>
          </div>
          ) : null}

          <Field label="Notes" htmlFor="notes">
            <Textarea id="notes" name="notes" rows={3} placeholder="Anything worth remembering about this person." defaultValue={String(party.notes ?? "")} />
          </Field>

          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : editing ? "Save changes" : "Save client"}
            </Button>
            <Button variant="secondary" onClick={() => navigate(editing ? `/app/clients/${id}` : "/app/clients")}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
